import { supabase } from './client'
import type { Profile, UserSettings, CloudTask, CloudProject, CloudSection, CloudTag, CloudGoal, UserIntentions, UsagePurpose, UserIntent } from './types'

/**
 * Supabase Database Queries
 * CRUD operations for Profile, Settings, Tasks, and User Intentions
 */

// ==================== PROFILE ====================

/**
 * Get user profile
 */
export async function getProfile(userId: string): Promise<Profile | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    if (error) {
      console.error('[Supabase] Get profile error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Get profile error:', error)
    return null
  }
}

/**
 * Update user profile
 */
export async function updateProfile(
  userId: string,
  updates: Partial<Omit<Profile, 'id' | 'email' | 'created_at'>>
): Promise<boolean> {
  if (!supabase) return false

  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)

    if (error) {
      console.error('[Supabase] Update profile error:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('[Supabase] Update profile error:', error)
    return false
  }
}

// ==================== USER SETTINGS ====================

/**
 * Get user settings
 */
export async function getUserSettings(userId: string): Promise<UserSettings | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle() // Verwende maybeSingle() statt single() - gibt null zurück wenn keine Daten

    if (error) {
      console.error('[Supabase] Get settings error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Get settings error:', error)
    return null
  }
}

/**
 * Update user settings
 */
export async function updateUserSettings(
  userId: string,
  updates: Partial<Omit<UserSettings, 'id' | 'user_id' | 'created_at'>>
): Promise<boolean> {
  if (!supabase) return false

  try {
    const { error } = await supabase
      .from('user_settings')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId)

    if (error) {
      console.error('[Supabase] Update settings error:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('[Supabase] Update settings error:', error)
    return false
  }
}

// ==================== USER INTENTIONS ====================

/**
 * Get user intentions
 */
export async function getUserIntentions(userId: string): Promise<UserIntentions | null> {
  if (!supabase) return null

  try {
    const { data, error} = await supabase
      .from('user_intentions')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()

    if (error) {
      console.error('[Supabase] Get intentions error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Get intentions error:', error)
    return null
  }
}

export interface SaveUserIntentionsParams {
  usagePurposes?: UsagePurpose[]
  userIntents?: UserIntent[]
}

export interface SaveUserIntentionsResult {
  success: boolean
  error?: string
}

/**
 * Save or update user intentions
 * Supports partial updates - can save only usagePurposes, only userIntents, or both
 */
export async function saveUserIntentions(
  userId: string,
  params: SaveUserIntentionsParams
): Promise<SaveUserIntentionsResult> {
  if (!supabase) {
    return { success: false, error: 'Supabase not available' }
  }

  try {
    // Check if intentions already exist
    const existing = await getUserIntentions(userId)
    const now = new Date().toISOString()

    // Prepare update data with merge logic
    const updateData: any = {
      updated_at: now,
    }

    // Update usage_purposes if provided
    if (params.usagePurposes !== undefined) {
      updateData.usage_purposes = params.usagePurposes
      updateData.usage_purposes_updated_at = now
      if (!existing?.usage_purposes_completed_at) {
        updateData.usage_purposes_completed_at = now
      }
    }

    // Update user_intents if provided
    if (params.userIntents !== undefined) {
      updateData.user_intents = params.userIntents
      updateData.user_intents_updated_at = now
      if (!existing?.user_intents_completed_at) {
        updateData.user_intents_completed_at = now
      }
    }

    if (existing) {
      // Update existing record (partial update)
      const { error } = await supabase
        .from('user_intentions')
        .update(updateData)
        .eq('user_id', userId)

      if (error) {
        return { success: false, error: error.message }
      }
    } else {
      // Insert new record
      const { error } = await supabase
        .from('user_intentions')
        .insert({
          user_id: userId,
          ...updateData,
        })

      if (error) {
        return { success: false, error: error.message }
      }
    }

    return { success: true }
  } catch (error) {
    return { success: false, error: `Intentions save error: ${error}` }
  }
}

// ==================== TASKS (Cloud) ====================

/**
 * Get all tasks for user
 */
export async function getCloudTasks(userId: string, since?: Date): Promise<CloudTask[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('tasks')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('order', { ascending: true })

    if (since) {
      query = query.gte('updated_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      console.error('[Supabase] Get tasks error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get tasks error:', error)
    return []
  }
}

/**
 * Lightweight sync reconciliation counts via RPC.
 * Returns entity counts so the client can skip the expensive
 * "fetch all IDs" reconciliation when counts already match locally.
 */
export async function getCloudSyncCounts(userId: string): Promise<{
  taskCount: number
  projectCount: number
  sectionCount: number
  tagCount: number
  goalCount: number
} | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase.rpc('get_sync_counts', { p_user_id: userId })

    if (error || !data?.[0]) {
      console.error('[Supabase] Get sync counts error:', error)
      return null
    }

    return {
      taskCount: Number(data[0].task_count),
      projectCount: Number(data[0].project_count),
      sectionCount: Number(data[0].section_count),
      tagCount: Number(data[0].tag_count ?? 0),
      goalCount: Number(data[0].goal_count ?? 0),
    }
  } catch (error) {
    console.error('[Supabase] Get sync counts error:', error)
    return null
  }
}

/**
 * Get all cloud task IDs for a user (lightweight — only fetches id column).
 * Used for ID-set reconciliation to detect tasks missing from local DB.
 */
export async function getCloudTaskIds(userId: string): Promise<string[]> {
  if (!supabase) return []

  try {
    const { data, error } = await supabase
      .from('tasks')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)

    if (error) {
      console.error('[Supabase] Get task IDs error:', error)
      return []
    }

    return (data || []).map(row => row.id)
  } catch (error) {
    console.error('[Supabase] Get task IDs error:', error)
    return []
  }
}

/**
 * Get specific cloud tasks by their IDs.
 * Used for targeted fetch of tasks missing from local DB.
 */
export async function getCloudTasksByIds(userId: string, ids: string[]): Promise<CloudTask[]> {
  if (!supabase || ids.length === 0) return []

  try {
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)

    if (error) {
      console.error('[Supabase] Get tasks by IDs error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get tasks by IDs error:', error)
    return []
  }
}

/**
 * Create a new task
 */
export async function createCloudTask(
  userId: string,
  task: Omit<CloudTask, 'id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<CloudTask | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase
      .from('tasks')
      .insert({
        ...task,
        user_id: userId,
      })
      .select()
      .single()

    if (error) {
      console.error('[Supabase] Create task error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Create task error:', error)
    return null
  }
}

/**
 * Update a task. Returns the server-assigned updated_at timestamp on success,
 * or null on failure.
 */
export async function updateCloudTask(
  taskId: string,
  updates: Partial<Omit<CloudTask, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null

  try {
    let query = supabase
      .from('tasks')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', taskId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query
      .select('updated_at')
      .single()

    if (error) {
      console.error('[Supabase] Update task error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Update task error:', error)
    return null
  }
}

/**
 * Delete a task (soft delete)
 */
export async function deleteCloudTask(taskId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false

  try {
    let query = supabase
      .from('tasks')
      .update({
        deleted_at: new Date().toISOString(),
      })
      .eq('id', taskId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query

    if (error) {
      console.error('[Supabase] Delete task error:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('[Supabase] Delete task error:', error)
    return false
  }
}

/**
 * Get cloud tasks that were deleted since a given date
 */
export async function getDeletedCloudTasks(userId: string, since?: Date): Promise<{ id: string; local_id: string | null }[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('tasks')
      .select('id, local_id')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)

    if (since) {
      query = query.gte('deleted_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      console.error('[Supabase] Get deleted tasks error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get deleted tasks error:', error)
    return []
  }
}

/**
 * Hard delete a task
 */
export async function hardDeleteCloudTask(taskId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false

  try {
    let query = supabase.from('tasks').delete().eq('id', taskId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query

    if (error) {
      console.error('[Supabase] Hard delete task error:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('[Supabase] Hard delete task error:', error)
    return false
  }
}

// ==================== PROJECTS (Cloud) ====================

/**
 * Get all projects for user
 */
export async function getCloudProjects(userId: string, since?: Date): Promise<CloudProject[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('order', { ascending: true })

    if (since) {
      query = query.gte('updated_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      console.error('[Supabase] Get projects error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get projects error:', error)
    return []
  }
}

/**
 * Get all cloud project IDs for a user (lightweight — only fetches id column).
 * Used for ID-set reconciliation to detect projects missing from local DB.
 */
export async function getCloudProjectIds(userId: string): Promise<string[]> {
  if (!supabase) return []

  try {
    const { data, error } = await supabase
      .from('projects')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)

    if (error) {
      console.error('[Supabase] Get project IDs error:', error)
      return []
    }

    return (data || []).map(row => row.id)
  } catch (error) {
    console.error('[Supabase] Get project IDs error:', error)
    return []
  }
}

/**
 * Get specific cloud projects by their IDs.
 * Used for targeted fetch of projects missing from local DB.
 */
export async function getCloudProjectsByIds(userId: string, ids: string[]): Promise<CloudProject[]> {
  if (!supabase || ids.length === 0) return []

  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)

    if (error) {
      console.error('[Supabase] Get projects by IDs error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get projects by IDs error:', error)
    return []
  }
}

/**
 * Get cloud projects that were deleted since a given date
 */
export async function getDeletedCloudProjects(userId: string, since?: Date): Promise<{ id: string; local_id: string | null }[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('projects')
      .select('id, local_id')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)

    if (since) {
      query = query.gte('deleted_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      console.error('[Supabase] Get deleted projects error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get deleted projects error:', error)
    return []
  }
}

/**
 * Create a new project
 */
export async function createCloudProject(
  userId: string,
  project: Omit<CloudProject, 'id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<CloudProject | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase
      .from('projects')
      .insert({
        ...project,
        user_id: userId,
      })
      .select()
      .single()

    if (error) {
      console.error('[Supabase] Create project error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Create project error:', error)
    return null
  }
}

/**
 * Update a project
 */
export async function updateCloudProject(
  projectId: string,
  updates: Partial<Omit<CloudProject, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null

  try {
    let query = supabase
      .from('projects')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', projectId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query
      .select('updated_at')
      .single()

    if (error) {
      console.error('[Supabase] Update project error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Update project error:', error)
    return null
  }
}

/**
 * Delete a project (soft delete)
 */
export async function deleteCloudProject(projectId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false

  try {
    let query = supabase
      .from('projects')
      .update({
        deleted_at: new Date().toISOString(),
      })
      .eq('id', projectId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query

    if (error) {
      console.error('[Supabase] Delete project error:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('[Supabase] Delete project error:', error)
    return false
  }
}

// ==================== SECTIONS (Cloud) ====================

export async function getCloudSections(userId: string, since?: Date): Promise<CloudSection[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('sections')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('order', { ascending: true })

    if (since) {
      query = query.gte('updated_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      console.error('[Supabase] Get sections error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get sections error:', error)
    return []
  }
}

/**
 * Get all cloud section IDs for a user (lightweight — only fetches id column).
 * Used for ID-set reconciliation to detect sections missing from local DB.
 */
export async function getCloudSectionIds(userId: string): Promise<string[]> {
  if (!supabase) return []

  try {
    const { data, error } = await supabase
      .from('sections')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)

    if (error) {
      console.error('[Supabase] Get section IDs error:', error)
      return []
    }

    return (data || []).map(row => row.id)
  } catch (error) {
    console.error('[Supabase] Get section IDs error:', error)
    return []
  }
}

/**
 * Get specific cloud sections by their IDs.
 * Used for targeted fetch of sections missing from local DB.
 */
export async function getCloudSectionsByIds(userId: string, ids: string[]): Promise<CloudSection[]> {
  if (!supabase || ids.length === 0) return []

  try {
    const { data, error } = await supabase
      .from('sections')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)

    if (error) {
      console.error('[Supabase] Get sections by IDs error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get sections by IDs error:', error)
    return []
  }
}

export async function getDeletedCloudSections(userId: string, since?: Date): Promise<{ id: string; local_id: string | null }[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('sections')
      .select('id, local_id')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)

    if (since) {
      query = query.gte('deleted_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      console.error('[Supabase] Get deleted sections error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get deleted sections error:', error)
    return []
  }
}

export async function createCloudSection(
  userId: string,
  section: Omit<CloudSection, 'id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<CloudSection | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase
      .from('sections')
      .insert({
        ...section,
        user_id: userId,
      })
      .select()
      .single()

    if (error) {
      console.error('[Supabase] Create section error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Create section error:', error)
    return null
  }
}

export async function updateCloudSection(
  sectionId: string,
  updates: Partial<Omit<CloudSection, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null

  try {
    let query = supabase
      .from('sections')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', sectionId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query
      .select('updated_at')
      .single()

    if (error) {
      console.error('[Supabase] Update section error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Update section error:', error)
    return null
  }
}

export async function deleteCloudSection(sectionId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false

  try {
    let query = supabase
      .from('sections')
      .update({
        deleted_at: new Date().toISOString(),
      })
      .eq('id', sectionId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query

    if (error) {
      console.error('[Supabase] Delete section error:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('[Supabase] Delete section error:', error)
    return false
  }
}

// ==================== TAGS (Cloud) ====================

/**
 * Get all tags for user
 */
export async function getCloudTags(userId: string, since?: Date): Promise<CloudTag[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('tags')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (since) {
      query = query.gte('updated_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      console.error('[Supabase] Get tags error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get tags error:', error)
    return []
  }
}

/**
 * Get all cloud tag IDs for a user (lightweight — only fetches id column).
 * Used for ID-set reconciliation to detect tags missing from local DB.
 */
export async function getCloudTagIds(userId: string): Promise<string[]> {
  if (!supabase) return []

  try {
    const { data, error } = await supabase
      .from('tags')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)

    if (error) {
      console.error('[Supabase] Get tag IDs error:', error)
      return []
    }

    return (data || []).map(row => row.id)
  } catch (error) {
    console.error('[Supabase] Get tag IDs error:', error)
    return []
  }
}

/**
 * Get specific cloud tags by their IDs.
 * Used for targeted fetch of tags missing from local DB.
 */
export async function getCloudTagsByIds(userId: string, ids: string[]): Promise<CloudTag[]> {
  if (!supabase || ids.length === 0) return []

  try {
    const { data, error } = await supabase
      .from('tags')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)

    if (error) {
      console.error('[Supabase] Get tags by IDs error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get tags by IDs error:', error)
    return []
  }
}

/**
 * Get cloud tags that were deleted since a given date
 */
export async function getDeletedCloudTags(userId: string, since?: Date): Promise<{ id: string; local_id: string | null }[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('tags')
      .select('id, local_id')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)

    if (since) {
      query = query.gte('deleted_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      console.error('[Supabase] Get deleted tags error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get deleted tags error:', error)
    return []
  }
}

/**
 * Create a new tag
 */
export async function createCloudTag(
  userId: string,
  tag: Omit<CloudTag, 'id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<CloudTag | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase
      .from('tags')
      .insert({
        ...tag,
        user_id: userId,
      })
      .select()
      .single()

    if (error) {
      console.error('[Supabase] Create tag error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Create tag error:', error)
    return null
  }
}

/**
 * Update a tag. Returns the server-assigned updated_at timestamp on success,
 * or null on failure.
 */
export async function updateCloudTag(
  tagId: string,
  updates: Partial<Omit<CloudTag, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null

  try {
    let query = supabase
      .from('tags')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', tagId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query
      .select('updated_at')
      .single()

    if (error) {
      console.error('[Supabase] Update tag error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Update tag error:', error)
    return null
  }
}

/**
 * Delete a tag (soft delete)
 */
export async function deleteCloudTag(tagId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false

  try {
    let query = supabase
      .from('tags')
      .update({
        deleted_at: new Date().toISOString(),
      })
      .eq('id', tagId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query

    if (error) {
      console.error('[Supabase] Delete tag error:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('[Supabase] Delete tag error:', error)
    return false
  }
}

// ==================== GOALS (Cloud) ====================

/**
 * Get all goals for user. Ordered newest-first to match the Ziele page.
 */
export async function getCloudGoals(userId: string, since?: Date): Promise<CloudGoal[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('goals')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })

    if (since) {
      query = query.gte('updated_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      console.error('[Supabase] Get goals error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get goals error:', error)
    return []
  }
}

/**
 * Get all cloud goal IDs for a user (lightweight — only fetches id column).
 * Used for ID-set reconciliation to detect goals missing from local DB.
 */
export async function getCloudGoalIds(userId: string): Promise<string[]> {
  if (!supabase) return []

  try {
    const { data, error } = await supabase
      .from('goals')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)

    if (error) {
      console.error('[Supabase] Get goal IDs error:', error)
      return []
    }

    return (data || []).map(row => row.id)
  } catch (error) {
    console.error('[Supabase] Get goal IDs error:', error)
    return []
  }
}

/**
 * Get specific cloud goals by their IDs.
 * Used for targeted fetch of goals missing from local DB.
 */
export async function getCloudGoalsByIds(userId: string, ids: string[]): Promise<CloudGoal[]> {
  if (!supabase || ids.length === 0) return []

  try {
    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)

    if (error) {
      console.error('[Supabase] Get goals by IDs error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get goals by IDs error:', error)
    return []
  }
}

/**
 * Get cloud goals that were deleted since a given date
 */
export async function getDeletedCloudGoals(userId: string, since?: Date): Promise<{ id: string; local_id: string | null }[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('goals')
      .select('id, local_id')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)

    if (since) {
      query = query.gte('deleted_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      console.error('[Supabase] Get deleted goals error:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[Supabase] Get deleted goals error:', error)
    return []
  }
}

/**
 * Create a new goal
 */
export async function createCloudGoal(
  userId: string,
  goal: Omit<CloudGoal, 'id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<CloudGoal | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase
      .from('goals')
      .insert({
        ...goal,
        user_id: userId,
      })
      .select()
      .single()

    if (error) {
      console.error('[Supabase] Create goal error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Create goal error:', error)
    return null
  }
}

/**
 * Update a goal. Returns the server-assigned updated_at timestamp on success,
 * or null on failure.
 */
export async function updateCloudGoal(
  goalId: string,
  updates: Partial<Omit<CloudGoal, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null

  try {
    let query = supabase
      .from('goals')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', goalId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query
      .select('updated_at')
      .single()

    if (error) {
      console.error('[Supabase] Update goal error:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[Supabase] Update goal error:', error)
    return null
  }
}

/**
 * Delete a goal (soft delete)
 */
export async function deleteCloudGoal(goalId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false

  try {
    let query = supabase
      .from('goals')
      .update({
        deleted_at: new Date().toISOString(),
      })
      .eq('id', goalId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query

    if (error) {
      console.error('[Supabase] Delete goal error:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('[Supabase] Delete goal error:', error)
    return false
  }
}

// ==================== REALTIME SUBSCRIPTIONS ====================

/**
 * Subscribe to task changes for a user via Supabase Realtime.
 * Returns an unsubscribe function for cleanup.
 */
export function subscribeToTasks(
  userId: string,
  callback: () => void,
  onStatus?: (status: string) => void
): () => void {
  if (!supabase) return () => {}

  const channel = supabase
    .channel(`tasks_${userId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'tasks', filter: `user_id=eq.${userId}` },
      callback
    )
    .subscribe((status, err) => {
      if (err) console.warn('[Realtime] Tasks channel error:', err.message)
      onStatus?.(status)
    })

  return () => { supabase!.removeChannel(channel) }
}

/**
 * Subscribe to project changes for a user via Supabase Realtime.
 * Returns an unsubscribe function for cleanup.
 */
export function subscribeToProjects(
  userId: string,
  callback: () => void,
  onStatus?: (status: string) => void
): () => void {
  if (!supabase) return () => {}

  const channel = supabase
    .channel(`projects_${userId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'projects', filter: `user_id=eq.${userId}` },
      callback
    )
    .subscribe((status, err) => {
      if (err) console.warn('[Realtime] Projects channel error:', err.message)
      onStatus?.(status)
    })

  return () => { supabase!.removeChannel(channel) }
}

/**
 * Subscribe to section changes for a user via Supabase Realtime.
 * Returns an unsubscribe function for cleanup.
 */
export function subscribeToSections(
  userId: string,
  callback: () => void,
  onStatus?: (status: string) => void
): () => void {
  if (!supabase) return () => {}

  const channel = supabase
    .channel(`sections_${userId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'sections', filter: `user_id=eq.${userId}` },
      callback
    )
    .subscribe((status, err) => {
      if (err) console.warn('[Realtime] Sections channel error:', err.message)
      onStatus?.(status)
    })

  return () => { supabase!.removeChannel(channel) }
}
